// Tracks which inquiries the admin has already opened, so we can highlight
// NEW (unseen) items vs ones already reviewed. Explicit clear-on-open model:
// an item stays "New" until the admin actually opens it.
// Scope: per browser (single super-admin). Persisted in localStorage.

import { useState, useEffect } from "react";

const KEY = (section) => `sr_seen_${section}`;
export const SEEN_EVENT = "sr-seen-changed";

function read(section) {
  try {
    const raw = localStorage.getItem(KEY(section));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function write(section, set) {
  try {
    localStorage.setItem(KEY(section), JSON.stringify([...set]));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export function isSeen(section, id) {
  if (!id) return true;
  return read(section).has(id);
}

export function markSeen(section, id) {
  if (!id) return;
  const set = read(section);
  if (set.has(id)) return;
  set.add(id);
  write(section, set);
  window.dispatchEvent(new CustomEvent(SEEN_EVENT, { detail: { section } }));
}

// Count of items that are NEW (unseen). `predicate` (optional) further limits
// which items are eligible to be "new" (e.g. chats must be unread_from_user).
export function newCount(section, items, idFn = (x) => x.id, predicate = () => true) {
  const set = read(section);
  return (items || []).reduce(
    (n, it) => (predicate(it) && !set.has(idFn(it)) ? n + 1 : n),
    0,
  );
}

// Re-render helper: bumps a counter whenever anything is marked seen, so
// components reading isSeen()/newCount() recompute immediately.
export function useSeenVersion() {
  const [v, setV] = useState(0);
  useEffect(() => {
    const h = () => setV((x) => x + 1);
    window.addEventListener(SEEN_EVENT, h);
    return () => window.removeEventListener(SEEN_EVENT, h);
  }, []);
  return v;
}
